/**
 * Ponte entre o TEXTO SALVO e o HTML EDITÁVEL (jul/2026).
 *
 * O sistema guarda comentário/descrição como markdown leve — `**negrito**`,
 * `*itálico*`, `==grifado==`, `{azul}texto{/azul}`, `![](url)` — e quem exibe é
 * o `renderTextoRico` (utils/textoComLinks.tsx). Isso NÃO muda: milhares de
 * comentários já estão gravados assim, e o portal do cliente lê o mesmo campo.
 *
 * O que muda é só a EDIÇÃO: o campo virou contenteditable, então a pessoa vê a
 * palavra já vermelha em vez de `{vermelho}palavra{/vermelho}` (o Ricardo
 * relatou que os códigos confundiam a equipe). Estas duas funções fazem a
 * tradução nos dois sentidos:
 *
 *   markupParaHtml : texto salvo  → HTML que aparece no editor
 *   htmlParaMarkup : HTML editado → texto salvo
 *
 * REGRA DE OURO: `htmlParaMarkup(markupParaHtml(x)) === x`. Se um dia alguém
 * adicionar uma marcação nova no renderer, tem que adicionar NOS DOIS lados
 * aqui — senão a formatação some ao editar um comentário existente.
 */

/** Mesmas cores do renderer e da barra de formatação. */
export const COR_HEX: Record<string, string> = {
  vermelho: "#dc2626",
  verde: "#16a34a",
  azul: "#2563eb",
  laranja: "#ea580c",
  roxo: "#9333ea",
};

/** Amarelo do `==grifado==` (yellow-200), usado no hiliteColor do editor. */
export const COR_GRIFO = "#fef08a";

const HEX_PARA_COR: Record<string, string> = Object.fromEntries(
  Object.entries(COR_HEX).map(([nome, hex]) => [hex.toLowerCase(), nome]),
);

function escapar(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

/** Hex de 6 dígitos — é o que torna cor livre segura pra entrar num style. */
export const RE_HEX = /^#[0-9a-fA-F]{6}$/;

/**
 * "rgb(220, 38, 38)" → "#dc2626". Devolve o próprio valor se já vier hex.
 *
 * TRANSPARENTE VIRA "" de propósito: `rgba(0,0,0,0)` é o fundo padrão de todo
 * elemento, e sem esse cuidado ele viraria "#000000" — indistinguível de preto
 * de verdade, e a barra acharia que TODO texto está grifado.
 */
export function rgbParaHex(cor: string): string {
  const s = (cor || "").trim();
  if (!s || s === "transparent") return "";
  if (s.startsWith("#")) return s.toLowerCase();
  const m = s.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)(?:\s*,\s*([\d.]+))?/i);
  if (!m) return "";
  if (m[4] !== undefined && Number(m[4]) === 0) return "";
  const h = (n: string) => Number(n).toString(16).padStart(2, "0");
  return `#${h(m[1])}${h(m[2])}${h(m[3])}`.toLowerCase();
}

// Regexes de LINHA (2ª leva jul/2026), iguais às do renderTextoRico:
// `- item` lista • `1. item` numerada. (checklist `- [ ]` cai como bullet no
// EDITOR por enquanto — exibe como caixa via renderTextoRico; botão próprio vem
// depois.)
const RE_CHECK = /^- \[([ xX])\]\s?(.*)$/;      // checklist (testar ANTES do bullet!)
const RE_BULLET = /^[-*]\s+(.*)$/;
const RE_NUM = /^\d+\.\s+(.*)$/;
const ehLista = (l: string) => RE_CHECK.test(l) || RE_BULLET.test(l) || RE_NUM.test(l);

/**
 * Caixa de checklist do EDITOR — contenteditable=false pra o cursor "pular" ela
 * e o clique ser tratado como toggle (ver MentionTextarea). Sem espaço colado:
 * o afastamento vem do margin-right, então htmlParaMarkup não gera espaço duplo.
 */
export function boxChecklistHtml(marcado: boolean): string {
  return `<span class="rt-box" contenteditable="false" data-checked="${marcado ? "1" : "0"}" style="cursor:pointer;user-select:none;margin-right:4px">${marcado ? "☑" : "☐"}</span>`;
}

/**
 * Converte UMA LINHA de markup em HTML inline (sem tratar \n — as linhas já
 * vêm separadas por markupParaHtml). `**` antes de `*`; todo texto solto é
 * escapado (anti-XSS no editor).
 */
function inlineParaHtml(texto: string): string {
  let out = "";
  let resto = texto || "";
  while (resto.length > 0) {
    let m = /^!\[([^\]]*)\]\(([^)\s]+)\)/.exec(resto);
    if (m) { out += `<img src="${escapar(m[2])}" alt="${escapar(m[1])}" />`; resto = resto.slice(m[0].length); continue; }
    m = /^\{(vermelho|verde|azul|laranja|roxo)\}([\s\S]*?)\{\/\1\}/.exec(resto);
    if (m) { out += `<span style="color: ${COR_HEX[m[1]]}">${inlineParaHtml(m[2])}</span>`; resto = resto.slice(m[0].length); continue; }
    m = /^\{(#[0-9a-fA-F]{6})\}([\s\S]*?)\{\/\1\}/.exec(resto);
    if (m) { out += `<span style="color: ${m[1].toLowerCase()}">${inlineParaHtml(m[2])}</span>`; resto = resto.slice(m[0].length); continue; }
    // {grande}/{pequeno} — tamanho de fonte
    m = /^\{(grande|pequeno)\}([\s\S]*?)\{\/\1\}/.exec(resto);
    if (m) { out += `<span style="font-size: ${m[1] === "grande" ? "1.3em" : "0.82em"}">${inlineParaHtml(m[2])}</span>`; resto = resto.slice(m[0].length); continue; }
    m = /^==([^=\n]+?)==/.exec(resto);
    if (m) { out += `<span style="background-color: ${COR_GRIFO}">${inlineParaHtml(m[1])}</span>`; resto = resto.slice(m[0].length); continue; }
    m = /^\*\*([\s\S]+?)\*\*/.exec(resto);
    if (m) { out += `<b>${inlineParaHtml(m[1])}</b>`; resto = resto.slice(m[0].length); continue; }
    m = /^\*([^*\n]+?)\*/.exec(resto);
    if (m) { out += `<i>${inlineParaHtml(m[1])}</i>`; resto = resto.slice(m[0].length); continue; }
    m = /^`([^`\n]+?)`/.exec(resto);
    if (m) { out += `<code>${escapar(m[1])}</code>`; resto = resto.slice(m[0].length); continue; }
    m = /^[^*=`{!]+/.exec(resto);
    if (m) { out += escapar(m[0]); resto = resto.slice(m[0].length); continue; }
    out += escapar(resto[0]); resto = resto.slice(1);
  }
  return out;
}

// ---------------------------------------------------------------------------
// markup → HTML
// ---------------------------------------------------------------------------

/**
 * Converte o texto salvo em HTML pro contenteditable. LINE-BASED (2ª leva):
 * agrupa linhas `- ` numa `<ul>`, `1. ` numa `<ol>` (é o que o execCommand do
 * editor produz), e o resto vira linhas normais separadas por <br>.
 */
export function markupParaHtml(texto: string): string {
  const linhas = (texto || "").split("\n");
  const partes: string[] = [];
  let i = 0;
  while (i < linhas.length) {
    // Checklist ANTES do bullet (`- [ ]` também casa `- `). Cada item = 1 div
    // com a caixa clicável; Enter cria linha normal (simples e robusto).
    if (RE_CHECK.test(linhas[i])) {
      while (i < linhas.length && RE_CHECK.test(linhas[i])) {
        const m = RE_CHECK.exec(linhas[i])!;
        partes.push(`<div>${boxChecklistHtml(m[1].toLowerCase() === "x")}${inlineParaHtml(m[2])}</div>`);
        i++;
      }
      continue;
    }
    if (RE_NUM.test(linhas[i])) {
      let lis = "";
      while (i < linhas.length && RE_NUM.test(linhas[i])) { lis += `<li>${inlineParaHtml(RE_NUM.exec(linhas[i])![1])}</li>`; i++; }
      partes.push(`<ol>${lis}</ol>`);
      continue;
    }
    if (RE_BULLET.test(linhas[i])) {
      let lis = "";
      while (i < linhas.length && RE_BULLET.test(linhas[i])) { lis += `<li>${inlineParaHtml(RE_BULLET.exec(linhas[i])![1])}</li>`; i++; }
      partes.push(`<ul>${lis}</ul>`);
      continue;
    }
    const normais: string[] = [];
    while (i < linhas.length && !ehLista(linhas[i])) { normais.push(inlineParaHtml(linhas[i])); i++; }
    partes.push(normais.join("<br>"));
  }
  return partes.join("");
}

// ---------------------------------------------------------------------------
// HTML → markup
// ---------------------------------------------------------------------------

type Marcas = { negrito: boolean; italico: boolean; grifo: boolean; cor: string; fonte: string };

const SEM_MARCA: Marcas = { negrito: false, italico: false, grifo: false, cor: "", fonte: "" };

/** font-size inline → "grande" | "pequeno" | "" (só os 2 níveis que a barra aplica). */
function fonteDoStyle(fs: string): string {
  const s = (fs || "").trim().toLowerCase();
  const m = /^([\d.]+)(em|rem|px|%)?$/.exec(s);
  if (!m) return "";
  const v = parseFloat(m[1]);
  const u = m[2] || "em";
  if (u === "em" || u === "rem") return v >= 1.15 ? "grande" : v <= 0.9 ? "pequeno" : "";
  if (u === "px") return v >= 18 ? "grande" : v <= 12 ? "pequeno" : "";
  if (u === "%") return v >= 115 ? "grande" : v <= 90 ? "pequeno" : "";
  return "";
}

/** Marcas que ESTE elemento adiciona (tag ou style inline do execCommand). */
function marcasDoElemento(el: HTMLElement): Marcas {
  const tag = el.tagName;
  const st = el.style;
  const peso = st.fontWeight || "";
  const negrito = tag === "B" || tag === "STRONG" || peso === "bold" || Number(peso) >= 600;
  const italico = tag === "I" || tag === "EM" || st.fontStyle === "italic";
  const fundo = rgbParaHex(st.backgroundColor || "");
  const grifo = tag === "MARK" || (!!fundo && fundo !== "#ffffff");
  // Cor nomeada quando o hex é um dos 5 da paleta; senão guarda o HEX puro
  // (cor livre do arco-íris). Só entra aqui style INLINE — a cor herdada do
  // tema não vira marcação, que é o que faz "sem cor" continuar sem cor.
  const hex = rgbParaHex(st.color || "");
  const cor = HEX_PARA_COR[hex] || (RE_HEX.test(hex) ? hex : "");
  const fonte = fonteDoStyle(st.fontSize || "");
  return { negrito, italico, grifo, cor, fonte };
}

/**
 * Serializa o conteúdo do editor de volta pro texto salvo.
 *
 * As marcas são acumuladas descendo a árvore e aplicadas de fora pra dentro na
 * ordem cor → grifo → negrito → itálico, pra a saída ficar sempre igual pro
 * mesmo conteúdo (round-trip estável).
 */
export function htmlParaMarkup(raiz: HTMLElement): string {
  let saida = "";

  const envolver = (txt: string, m: Marcas): string => {
    if (!txt) return "";
    let r = txt;
    if (m.italico) r = `*${r}*`;
    if (m.negrito) r = `**${r}**`;
    if (m.grifo) r = `==${r}==`;
    if (m.cor) r = `{${m.cor}}${r}{/${m.cor}}`;
    if (m.fonte) r = `{${m.fonte}}${r}{/${m.fonte}}`;
    return r;
  };

  const anda = (no: Node, herdadas: Marcas) => {
    if (no.nodeType === Node.TEXT_NODE) {
      //   (nbsp) vira espaço normal: o contenteditable cria nbsp sozinho e
      // ele apareceria como caractere estranho no texto salvo.
      const txt = (no.textContent || "").replace(/ /g, " ").replace(/​/g, "");
      saida += envolver(txt, herdadas);
      return;
    }
    if (no.nodeType !== Node.ELEMENT_NODE) return;

    const el = no as HTMLElement;
    const tag = el.tagName;

    // Caixa de checklist: o glifo (☐/☑) NUNCA vira texto — o estado sai no
    // prefixo `- [ ]`/`- [x]` do item (tratado no bloco DIV abaixo).
    if (el.classList.contains("rt-box")) return;

    if (tag === "BR") {
      saida += "\n";
      return;
    }
    if (tag === "IMG") {
      const src = el.getAttribute("src") || "";
      const alt = el.getAttribute("alt") || "";
      if (src) saida += `![${alt}](${src})`;
      return;
    }
    if (tag === "CODE") {
      saida += `\`${(el.textContent || "").replace(/ /g, " ")}\``;
      return;
    }

    // LISTAS (2ª leva): <ul>/<ol> viram linhas `- ` / `N. `. Cada <li> é uma
    // linha; renumera a ordenada. Precede/segue de \n pra separar do resto.
    if (tag === "UL" || tag === "OL") {
      if (saida && !saida.endsWith("\n")) saida += "\n";
      let n = 1;
      Array.from(el.children).forEach((li) => {
        if ((li as HTMLElement).tagName !== "LI") return;
        saida += tag === "OL" ? `${n++}. ` : "- ";
        (li as HTMLElement).childNodes.forEach((f) => anda(f, herdadas));
        if (!saida.endsWith("\n")) saida += "\n";
      });
      return;
    }

    // DIV/P criados pelo contenteditable = quebra de linha, menos no primeiro.
    const ehBloco = tag === "DIV" || tag === "P";
    if (ehBloco) {
      // CHECKLIST: div que começa com a caixa .rt-box vira `- [ ]`/`- [x] item`.
      const box = Array.from(el.children).find((c) => (c as HTMLElement).classList?.contains("rt-box")) as HTMLElement | undefined;
      if (box) {
        if (saida && !saida.endsWith("\n")) saida += "\n";
        saida += box.getAttribute("data-checked") === "1" ? "- [x] " : "- [ ] ";
        el.childNodes.forEach((f) => { if (f !== box) anda(f, herdadas); });
        if (!saida.endsWith("\n")) saida += "\n";
        return;
      }
      if (saida && !saida.endsWith("\n")) saida += "\n";
      // `<div><br></div>` é uma LINHA VAZIA: o bloco já contou a quebra, então
      // o <br> de dentro não pode contar de novo (senão cada Enter em linha
      // vazia viraria dois \n e o texto ia se esticando a cada edição).
      if (el.childNodes.length === 1 && (el.firstChild as HTMLElement)?.tagName === "BR") return;
    }

    const m = marcasDoElemento(el);
    const acumuladas: Marcas = {
      negrito: herdadas.negrito || m.negrito,
      italico: herdadas.italico || m.italico,
      grifo: herdadas.grifo || m.grifo,
      cor: m.cor || herdadas.cor,
      fonte: m.fonte || herdadas.fonte,
    };
    el.childNodes.forEach((f) => anda(f, acumuladas));
  };

  raiz.childNodes.forEach((f) => anda(f, SEM_MARCA));

  // O editor vazio deixa um <br> sobrando; não vale como conteúdo.
  return saida.replace(/\n+$/, "");
}
